import React from 'react';

import { DataClassifierType } from '@ariksa/data-scanning';
import { Box, HStack, Text } from '@chakra-ui/react';

import {
  StackedCell,
  timeLabel,
  timeTooltipLabel,
  WithResourceIcon,
} from 'components/DataDisplay';
import {
  CheckmarkCircleOutlineIcon,
  DisableIcon,
  IconTypes,
} from 'components/Icons';
import {
  getDataTypeNameColor,
  renderDataTypeLabels,
} from 'containers/Visibility/Data/Components/hooks/utils';

export const useDataClassifierTableColumns = () => {
  const updated = {
    header: 'Updated',
    align: 'left',
    render: ({ row }) =>
      !!row?.last_updated_by ? (
        <StackedCell
          upper={row?.last_updated_by}
          lower={timeLabel(row?.last_updated_at)}
          lowerTooltip={timeTooltipLabel(row?.last_updated_at)}
        />
      ) : (
        '-'
      ),
  };

  const created = {
    header: 'Created',
    align: 'left',
    render: ({ row }) => (
      <StackedCell
        upper={row.created_by}
        lower={timeLabel(row.created_at)}
        lowerTooltip={timeTooltipLabel(row?.created_at)}
      />
    ),
    styles: { width: '5%', cell: { maxWidth: '5%' } },
  };

  const status = {
    header: 'Status',
    accessor: 'is_enabled',
    align: 'left',
    render: ({ value }) => (
      <HStack>
        <Box w={4} color={value ? 'green' : 'red'}>
          {value ? <CheckmarkCircleOutlineIcon /> : <DisableIcon />}
        </Box>
        <Box>{value ? 'Enabled' : 'Disabled'}</Box>
      </HStack>
    ),
  };

  const description = {
    header: 'Description',
    accessor: 'description',
    align: 'left',
    render: ({ row }) => (
      <Text whiteSpace="wrap" noOfLines={2}>
        {row?.description ?? '-'}
      </Text>
    ),
    styles: { width: '40%', cell: { maxWidth: '40%' } },
  };

  const dataExclusionColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      accessor: 'name',
      align: 'left',
      render: ({ row }) => (
        <WithResourceIcon resourceType={IconTypes.Disable}>
          <Box>{row.name}</Box>
        </WithResourceIcon>
      ),
    },
    description,
    status,
    {
      header: 'Type',
      accessor: 'type',
      align: 'left',
    },
    created,
    updated,
  ];

  const dataTypeColumns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Data Type</Box>,
      accessor: 'name',
      align: 'left',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={
            row.type === DataClassifierType.Data ? row.name : IconTypes.Policy
          }
          tooltip={row.read_name}
          color={getDataTypeNameColor(row)}
        >
          {row.read_name}
        </WithResourceIcon>
      ),
    },
    description,

    {
      header: 'Sensitivity',
      accessor: 'sensitivity_category',
      align: 'left',
    },
    {
      header: 'Label(s)',
      align: 'left',
      accessor: 'labels',
      render: ({ row }) =>
        row?.labels?.length ? renderDataTypeLabels({ row }) : 0,
    },
    created,
  ];

  return { dataTypeColumns, dataExclusionColumns };
};
